/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import {
  AppBarComponent,
  AppBarItem,
  AppBarLink,
  AppBarNavLink,
} from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { Link } from 'react-router-dom';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import {
  queryHelpers,
  TextAction,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { UserDropdown } from 'components/header-nav/user-dropdown/user-dropdown.component';
import { MobileNav } from 'components/header-nav/mobile-nav/mobile-nav.component';
import {
  LinkType,
  NavLink,
  useNavLinksList,
} from 'hooks/use-nav-links-list/use-nav-links-list.hook';
import {
  PAGE_NAVIGATION_WIDTH,
  PAGE_NAVIGATION_HEIGHT,
} from 'common/constants/ui';
import { Logo } from 'components/logo.component';
import { ROUTE_PATHS } from 'common/constants/constants';

export const AppNavBarComponent = React.memo(() => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const isAboveLaptop = useMediaQuery(queryHelpers.up('laptop'));

  const { formatMessage } = useIntl();
  const { navLinks } = useNavLinksList();
  const sendNavBarAnalyticsEvent = (action: string) => () =>
    trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
      screen_name: 'Navigation Bar',
      detail: action,
    });

  const navLinkWrapper = (navLink: NavLink) => {
    switch (navLink.type) {
      case LinkType.LINK:
        return (
          <AppBarLink
            as={Link}
            to={navLink.to}
            key={navLink.message_id}
            onClick={sendNavBarAnalyticsEvent(navLink.message_id)}
            color="onSurface.text.primary"
            hoverStyle={{ color: 'interactive.action.primary' }}
            fontWeight="semibold"
          >
            {formatMessage({ id: navLink.message_id })}
          </AppBarLink>
        );
      case LinkType.NAV:
        return (
          <AppBarNavLink
            to={navLink.to}
            key={navLink.message_id}
            onClick={sendNavBarAnalyticsEvent(navLink.message_id)}
            color="onSurface.text.primary"
            hoverStyle={{ color: 'interactive.action.primary' }}
            decorativeBarColor="interactive.action.primary"
            fontWeight="semibold"
          >
            {formatMessage({ id: navLink.message_id })}
          </AppBarNavLink>
        );
      default:
        throw new Error('invalid link type');
    }
  };
  return (
    <AppBarComponent
      navHeight={PAGE_NAVIGATION_HEIGHT}
      headerCSS={{
        ul: { maxWidth: PAGE_NAVIGATION_WIDTH, marginInline: 'auto' },
        backgroundColor: '$surfaceCardSecondary',
        paddingInline: !isAboveLaptop ? '$oneAndHalf' : '',
        justifyContent: 'space-between',
      }}
    >
      <AppBarItem
        flexGrow={{ _: 1 }}
        justifyContent="start"
        alignItems="center"
      >
        <TextAction as={Link} to={ROUTE_PATHS.HOME}>
          <Logo width={98} />
        </TextAction>
      </AppBarItem>
      {isAboveLaptop ? (
        <>
          {/* Spacer */}
          <AppBarItem flexGrow={{ _: 0, laptop: 2 }} aria-hidden="true" />
          {/* App Nav Home & Capability Areas */}
          {navLinks.map((navLink) => navLinkWrapper(navLink))}

          {/* Spacer */}
          <AppBarItem flexGrow={{ _: 1, laptop: 30 }} />

          {/* App Nav User Dropdown */}
          <AppBarItem
            alignItems="center"
            justifyContent="end"
            css={{
              '#user_dropdown_menu .user-dropdown-item_link': {
                fontWeight: '700',
              },

              '#user_dropdown_menu .user-dropdown_divider': {
                borderBlockEnd: 'none',
                borderColor: '#DADADA',
              },

              '.user-dropdown-item_link > div': {
                justifyContent: 'space-between',
              },
            }}
          >
            <UserDropdown />
          </AppBarItem>
        </>
      ) : (
        // Mobile Navigation
        <MobileNav
          open={menuOpen}
          onOpenChange={() => {
            setMenuOpen((previous) => !previous);
          }}
          closeModal={() => setMenuOpen(false)}
        />
      )}
    </AppBarComponent>
  );
});
